import { ResponseCode } from '@/common/request/types';
import { useAppRouter } from '@/common/route/useAppRouter';
import { useCrudActions } from '@/common/view/useCrudActions';
import { useLocale } from '@/modules/locale/model/locale';
import { ROUTE_NAME_QUEST } from '@/modules/quest/router';
import { questService } from '@/services/quest';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { reactive } from 'vue';
import { useRoute } from 'vue-router';

export function useQuestBoostTranslation() {
    const router = useAppRouter();
    const route = useRoute();
    const crudAction = useCrudActions();

    const { localeSupportedOptions, getListSupported } = useLocale();

    const model = reactive({
        itemId: route.params.boostId,
        locale: null,
        title: '',
        description: '',
        hasData: false
    });

    const validations = {
        title: { required },
        locale: { required }
    };

    const v$ = useVuelidate(validations, model);

    const beforeFetch = async () => {
        await getListSupported();
        if (!localeSupportedOptions.value.length) {
            router.push({ name: ROUTE_NAME_QUEST.BOOST.INDEX });
            return;
        }
    };

    const fetch = crudAction.executeAction({
        canExecute: () => model.locale != null,
        request: () => questService.boost.getText(model.itemId, model.locale),
        onSuccess: ({ data }) => {
            model.hasData = true;
            model.title = data.title;
            model.description = data.description;
        },
        onError: ({ code }) => {
            if (code === ResponseCode.NothingFound) {
                model.hasData = false;
                model.title = '';
                model.description = '';
            } else router.push({ name: ROUTE_NAME_QUEST.BOOST.INDEX });
        },
        onFinally: () => v$.value.$reset()
    });

    const actions = {
        updateText: crudAction.excuteAndNotify({
            validator: v$,
            request: () => questService.boost.updateText(model.itemId, model.locale, model.title, model.description),
            onSuccess: fetch
        }),
        removeText: crudAction.confirmAndRemove({
            request: () => questService.boost.removeText(model.itemId, model.locale),
            onSuccess: fetch
        })
    };

    return { model, v$, fetch, beforeFetch, actions, localeSupportedOptions };
}
